import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FaCopy, FaDownload } from "react-icons/fa";
import html2pdf from "html2pdf.js/dist/html2pdf.min.js";

function mdToHtml(md) {
  return String(md || "")
    .replace(/<h1(\b[^>]*)>/gi, "<h3$1>")
    .replace(/<\/h1>/gi, "</h3>")
    .replace(/<h2(\b[^>]*)>/gi, "<h4$1>")
    .replace(/<\/h2>/gi, "</h4>")
    .replace(/<h3(\b[^>]*)>/gi, "<h5$1>")
    .replace(/<\/h3>/gi, "</h5>");
}

function fmtDate(value) {
  if (!value || value === "None") return "No expiry";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

const CoHedgerSummaryTileComponent = ({
  loading,
  summary,
  currencyPair,
  assetType,
  executionType,
  expirationDate,
}) => {
  const [copied, setCopied] = useState(false);
  const summaryHtml = useMemo(() => mdToHtml(summary || ""), [summary]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(summary || "");
      setCopied(true);
    } catch (error) {
      console.error("Clipboard failed", error);
    }
  };

  const downloadPdf = () => {
    const element = document.getElementById("cohedger-report-pdf");
    if (!element) return;

    const fileName = `CoHedger_${currencyPair || "PAIR"}_${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;

    html2pdf()
      .set({
        margin: 10,
        filename: fileName,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(element)
      .save();
  };

  const metaItems = [
    { label: "Currency pair", value: currencyPair || "—" },
    { label: "Asset type", value: assetType || "—" },
    { label: "Execution side", value: executionType || "—" },
    { label: "Expiration", value: fmtDate(expirationDate) },
  ];

  return (
    <section className="ch-card ch-report-card">
      <header className="ch-report-head">
        <div>
          <h3 className="ch-title" style={{ fontSize: "0.98rem" }}>
            Detailed report
          </h3>
          <p className="ch-sub">Hedge rationale, market context and export-ready notes</p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button type="button" className="ch-icon-btn" disabled={!summary} onClick={copyToClipboard}>
            <FaCopy /> {copied ? "Copied" : "Copy"}
          </button>
          <button type="button" className="ch-icon-btn" disabled={!summary} onClick={downloadPdf}>
            <FaDownload /> PDF
          </button>
        </div>
      </header>

      <div className="ch-report-scroll">
        <div id="cohedger-report-pdf" className="ch-report-doc">
          <div className="ch-meta">
            {metaItems.map((item) => (
              <div key={item.label} className="ch-meta-item">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>

          {loading && !summary ? (
            <div className="ch-empty">
              <Spinner animation="border" size="sm" />
              <span>Streaming hedge analysis...</span>
            </div>
          ) : summary ? (
            <div className="ch-report-body" dangerouslySetInnerHTML={{ __html: summaryHtml }} />
          ) : (
            <div className="ch-empty">
              The hedge brief will appear here once the model finishes generating the report.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CoHedgerSummaryTileComponent;
